import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useAuditLogs } from '@/hooks/useAuditLogs'
import { Skeleton } from '@/components/ui/skeleton'
import { AuditLogDialog } from './AuditLogDialog'
import { Badge } from '@/components/ui/badge'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon, Filter, X } from 'lucide-react'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useViewMode } from '@/stores/viewMode'

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800',
  READ: 'bg-blue-100 text-blue-800',
  UPDATE: 'bg-yellow-100 text-yellow-800',
  DELETE: 'bg-red-100 text-red-800',
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: '新增',
  READ: '查詢',
  UPDATE: '更新',
  DELETE: '刪除',
}

export function AuditLogList() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)
  const { mode, id: viewModeId } = useViewMode()

  // When in provider mode, pass providerId to filter logs
  const providerId = mode === 'provider' && viewModeId ? viewModeId : undefined

  // Filters
  const [action, setAction] = useState<string>('')
  const [resourceType, setResourceType] = useState<string>('')
  const [searchUser, setSearchUser] = useState<string>('')
  const [ipAddress, setIpAddress] = useState<string>('')
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()

  const { logs, pagination, isLoading, isError } = useAuditLogs({
    page,
    pageSize,
    action: action || undefined,
    resource_type: resourceType || undefined,
    search_user: searchUser || undefined,
    ip_address: ipAddress || undefined,
    start_date: startDate ? startDate.toISOString() : undefined,
    end_date: endDate ? endDate.toISOString() : undefined,
    providerId,
  })

  const clearFilters = () => {
    setAction('')
    setResourceType('')
    setSearchUser('')
    setIpAddress('')
    setStartDate(undefined)
    setEndDate(undefined)
    setPage(1)
  }

  const hasFilters = action || resourceType || searchUser || ipAddress || startDate || endDate

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">載入操作日誌失敗。請稍後再試。</div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    )
  }

  const generatePaginationItems = () => {
    if (!pagination) return []
    const items = []
    const { page: currentPage, totalPages } = pagination

    items.push(1)
    if (currentPage > 3) items.push('ellipsis')

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) items.push('ellipsis')
    if (totalPages > 1) items.push(totalPages)

    return items
  }

  return (
    <>
      <Card>
        <CardContent className="p-6">
          {/* Filters */}
          <div className="mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Filter className="h-4 w-4" />
                篩選條件
              </h3>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  清除篩選
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">操作類型</label>
                <Select value={action || 'all'} onValueChange={(val) => setAction(val === 'all' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="全部" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="CREATE">新增</SelectItem>
                    <SelectItem value="READ">查詢</SelectItem>
                    <SelectItem value="UPDATE">更新</SelectItem>
                    <SelectItem value="DELETE">刪除</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">資源類型</label>
                <Input
                  placeholder="例如: user, clinic"
                  value={resourceType}
                  onChange={(e) => setResourceType(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">使用者</label>
                <Input
                  placeholder="搜尋使用者名稱或帳號"
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">IP 位址</label>
                <Input
                  placeholder="例如: 192.168.1.1"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">開始日期</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">結束日期</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">時間</th>
                    <th className="text-left p-3 font-medium">操作</th>
                    <th className="text-left p-3 font-medium">資源類型</th>
                    <th className="text-left p-3 font-medium">資源 ID</th>
                    <th className="text-left p-3 font-medium">使用者</th>
                    <th className="text-left p-3 font-medium">IP 位址</th>
                    <th className="text-left p-3 font-medium">端點</th>
                    <th className="text-left p-3 font-medium">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {logs && logs.length > 0 ? (
                    logs.map((log) => (
                      <tr
                        key={log.log_id}
                        className="border-t hover:bg-muted/30 cursor-pointer"
                        onClick={() => setSelectedLogId(log.log_id)}
                      >
                        <td className="p-3 text-sm">
                          {format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss')}
                        </td>
                        <td className="p-3">
                          <Badge className={ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'}>
                            {ACTION_LABELS[log.action] || log.action}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm">{log.resource_type}</td>
                        <td className="p-3 text-sm font-mono text-xs">{log.resource_id || '-'}</td>
                        <td className="p-3 text-sm">
                          {log.user ? (
                            <div>
                              <div>{log.user.user_name}</div>
                              <div className="text-xs text-muted-foreground">{log.user.account}</div>
                            </div>
                          ) : log.account ? (
                            <div>
                              <div className="text-muted-foreground italic">已刪除帳號</div>
                              <div className="text-xs text-muted-foreground">{log.account}</div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">系統</span>
                          )}
                        </td>
                        <td className="p-3 text-sm font-mono text-xs">{log.ip_address}</td>
                        <td className="p-3 text-sm font-mono text-xs">
                          <span className="text-muted-foreground">{log.http_method}</span>{' '}
                          {log.endpoint}
                        </td>
                        <td className="p-3 text-sm">
                          <Badge
                            variant={
                              log.response_status && log.response_status >= 200 && log.response_status < 300
                                ? 'default'
                                : 'destructive'
                            }
                          >
                            {log.response_status || '-'}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-muted-foreground">
                        沒有符合條件的操作日誌
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                共 {pagination.total} 筆，第 {pagination.page} / {pagination.totalPages} 頁
              </div>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage(Math.max(1, page - 1))}
                      className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>

                  {generatePaginationItems().map((item, index) =>
                    item === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <span className="px-3">...</span>
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationLink
                          onClick={() => setPage(Number(item))}
                          isActive={page === item}
                          className="cursor-pointer"
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                      className={
                        page === pagination.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedLogId && (
        <AuditLogDialog logId={selectedLogId} open={!!selectedLogId} onClose={() => setSelectedLogId(null)} />
      )}
    </>
  )
}
